import type { FilterFieldConfig, FilterParamSchema } from "./types"

// ── Single-field helpers ──────────────────────────────────────────────

/**
 * Serialize one field value to URL param(s).
 * Returns empty object when the value is empty/null (param should be omitted).
 * dateRange returns two entries; all other types return zero or one.
 */
export function serializeField<T>(
  value: T,
  config: FilterFieldConfig<T>,
): Record<string, string> {
  switch (config.type) {
    case "string": {
      if (!config.urlKey) {
        return {}
      }
      const str = value as string
      if (!str || str.trim() === "") {
        return {}
      }
      return { [config.urlKey]: str }
    }

    case "string[]": {
      if (!config.urlKey) {
        return {}
      }
      const arr = value as string[]
      if (!arr || arr.length === 0) {
        return {}
      }
      return { [config.urlKey]: arr.map(encodeURIComponent).join(",") }
    }

    case "dateRange": {
      if (!config.urlKeys) {
        return {}
      }
      const range = value as { start: Date | null; end: Date | null }
      if (!range) {
        return {}
      }
      const result: Record<string, string> = {}
      if (range.start && !isNaN(range.start.getTime())) {
        result[config.urlKeys.start] = range.start.toISOString()
      }
      if (range.end && !isNaN(range.end.getTime())) {
        result[config.urlKeys.end] = range.end.toISOString()
      }
      return result
    }

    case "sort": {
      if (!config.urlKey) {
        return {}
      }
      const sort = value as {
        column?: string | number
        direction?: string
      }
      if (!sort?.column) {
        return {}
      }
      return {
        [config.urlKey]: `${sort.column}:${sort.direction ?? "ascending"}`,
      }
    }

    case "custom": {
      if (!config.urlKey) {
        return {}
      }
      const serialized = config.serialize(value)
      if (!serialized) {
        return {}
      }
      return { [config.urlKey]: serialized }
    }
  }
}

/**
 * Parse one field value from URL query params.
 * Returns `undefined` when the relevant param(s) are absent.
 */
export function parseField<T>(
  query: Record<string, string | string[] | undefined>,
  config: FilterFieldConfig<T>,
): T | undefined {
  switch (config.type) {
    case "string": {
      if (!config.urlKey) {
        return undefined
      }
      const raw = query[config.urlKey]
      if (raw === undefined) {
        return undefined
      }
      return Array.isArray(raw) ? (raw[0] as T) : (raw as T)
    }

    case "string[]": {
      if (!config.urlKey) {
        return undefined
      }
      const raw = query[config.urlKey]
      if (raw === undefined) {
        return undefined
      }
      const values = Array.isArray(raw) ? raw : [raw]
      const items = values.flatMap((value) => value.split(",")).filter(Boolean)
      if (items.length === 0) {
        return undefined
      }
      return items.map((item) => {
        try {
          return decodeURIComponent(item)
        } catch {
          return item
        }
      }) as T
    }

    case "dateRange": {
      if (!config.urlKeys) {
        return undefined
      }
      const startRaw = query[config.urlKeys.start]
      const endRaw = query[config.urlKeys.end]
      if (startRaw === undefined && endRaw === undefined) {
        return undefined
      }

      const startStr = Array.isArray(startRaw) ? startRaw[0] : startRaw
      const endStr = Array.isArray(endRaw) ? endRaw[0] : endRaw

      const start = startStr ? new Date(startStr) : null
      const end = endStr ? new Date(endStr) : null

      if (start && isNaN(start.getTime())) {
        return undefined
      }
      if (end && isNaN(end.getTime())) {
        return undefined
      }

      return { start, end } as T
    }

    case "sort": {
      if (!config.urlKey) {
        return undefined
      }
      const raw = query[config.urlKey]
      if (raw === undefined) {
        return undefined
      }
      const str = Array.isArray(raw) ? raw[0] : raw
      if (!str) {
        return undefined
      }
      const colonIdx = str.lastIndexOf(":")
      if (colonIdx === -1) {
        return { column: str, direction: "ascending" } as T
      }
      const column = str.slice(0, colonIdx)
      const direction = str.slice(colonIdx + 1)
      if (!column) {
        return undefined
      }
      return {
        column,
        direction: direction === "descending" ? "descending" : "ascending",
      } as T
    }

    case "custom": {
      if (!config.urlKey) {
        return undefined
      }
      const raw = query[config.urlKey]
      if (raw === undefined) {
        return undefined
      }
      const str = Array.isArray(raw) ? raw[0] : raw
      if (!str) {
        return undefined
      }
      try {
        return config.parse(str)
      } catch {
        return undefined
      }
    }
  }
}

// ── Full-state helpers ────────────────────────────────────────────────

/**
 * Serialize all URL-synced fields in `state` to a flat param map.
 */

export function serializeToUrlParams<T extends object>(
  state: T,
  schema: FilterParamSchema<T>,
): Record<string, string> {
  const params: Record<string, string> = {}
  for (const key of Object.keys(schema) as (keyof T & string)[]) {
    Object.assign(params, serializeField(state[key], schema[key]))
  }
  return params
}

/**
 * Parse URL query into a partial state (only fields present in the URL).
 */

export function parseUrlParams<T extends object>(
  query: Record<string, string | string[] | undefined>,
  schema: FilterParamSchema<T>,
): Partial<T> {
  const result: Partial<T> = {}
  for (const key of Object.keys(schema) as (keyof T & string)[]) {
    const value = parseField(query, schema[key])
    if (value !== undefined) {
      result[key] = value as T[typeof key]
    }
  }
  return result
}

/**
 * Collect every URL param key the schema owns.
 * Used to distinguish "our" keys from keys belonging to other hooks/components.
 */

export function getOwnedUrlKeys<T extends object>(
  schema: FilterParamSchema<T>,
): Set<string> {
  const keys = new Set<string>()
  for (const config of Object.values(schema) as Array<FilterFieldConfig>) {
    if (config.type === "dateRange") {
      if (config.urlKeys) {
        keys.add(config.urlKeys.start)
        keys.add(config.urlKeys.end)
      }
    } else if ("urlKey" in config && config.urlKey) {
      keys.add(config.urlKey)
    }
  }
  return keys
}

// ── localStorage helpers ──────────────────────────────────────────────

/**
 * Serialize state to JSON for localStorage.
 * Dates are converted to ISO strings for safe storage.
 */

export function serializeForStorage<T extends object>(
  state: T,
  schema: FilterParamSchema<T>,
): string {
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(schema) as (keyof T & string)[]) {
    const config = schema[key]
    const value = state[key]

    if (config.type === "dateRange") {
      const range = value as { start: Date | null; end: Date | null }
      const isValidDate = (d: Date | null): d is Date =>
        d instanceof Date && !isNaN(d.getTime())
      out[key] = {
        start: isValidDate(range?.start) ? range.start.toISOString() : null,
        end: isValidDate(range?.end) ? range.end.toISOString() : null,
      }
    } else if (config.type === "custom") {
      // Custom fields are stored as plain JSON values in localStorage
      out[key] = value
    } else {
      out[key] = value
    }
  }
  return JSON.stringify(out)
}

/**
 * Deserialize state from localStorage JSON.
 * Uses schema type info to reconstruct Dates and validate arrays.
 */

export function deserializeFromStorage<T extends object>(
  json: string,
  schema: FilterParamSchema<T>,
  defaults: T,
): T {
  try {
    const parsed = JSON.parse(json) as Record<string, unknown>
    const result = { ...defaults }

    for (const key of Object.keys(schema) as (keyof T & string)[]) {
      if (!(key in parsed)) {
        continue
      }
      const raw = parsed[key]

      switch (schema[key].type) {
        case "dateRange": {
          if (raw && typeof raw === "object" && !Array.isArray(raw)) {
            const obj = raw as Record<string, unknown>
            const start =
              typeof obj.start === "string" ? new Date(obj.start) : null
            const end = typeof obj.end === "string" ? new Date(obj.end) : null
            result[key] = {
              start: start && !isNaN(start.getTime()) ? start : null,
              end: end && !isNaN(end.getTime()) ? end : null,
            } as T[typeof key]
          }
          break
        }
        case "string[]": {
          if (
            Array.isArray(raw) &&
            raw.every((item) => typeof item === "string")
          ) {
            result[key] = raw as T[typeof key]
          }
          break
        }
        case "sort": {
          if (raw && typeof raw === "object") {
            const obj = raw as Record<string, unknown>
            if (
              typeof obj.column === "string" ||
              typeof obj.column === "number"
            ) {
              result[key] = {
                column: obj.column,
                direction:
                  obj.direction === "descending" ? "descending" : "ascending",
              } as T[typeof key]
            }
          }
          break
        }
        case "string": {
          if (typeof raw === "string") {
            result[key] = raw as T[typeof key]
          }
          break
        }
        case "custom": {
          // Custom fields are stored as-is in JSON; pass through directly
          result[key] = raw as T[typeof key]
          break
        }
      }
    }

    return result
  } catch {
    return defaults
  }
}
